import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import {
  Check,
  ArrowRight,
  ArrowLeft,
  ShieldCheck,
  Info,
  Loader2,
  Sparkles,
  Lock,
  Hourglass,
  CheckCircle2,
  XCircle,
  CalendarDays,
  MessageCircle,
} from 'lucide-react';
import { Button, StatusChip } from '@lms/ui';
import { useRegistrationProgram } from '../hooks/usePrograms';
import { useRegisterProgram } from '../hooks/useRegistrations';
import { useProgramEnrollment } from '../hooks/useEnrollments';
import { useAuthStore } from '../store/auth';
import { useLanguage, type Bilingual as BilingualType } from '../lib/LanguageContext';
import type { ProgramRegistration } from '@lms/shared/types';
import { resolveMediaUrl } from '@lms/shared/utils';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';
import { EditProfileModal } from '../components/EditProfileModal';
import { CityAutocomplete } from '../components/CityAutocomplete';
import { apiBaseUrl } from '../lib/api-client';

type Step = 'PROGRAM' | 'PROFILE' | 'CONSENT' | 'SUCCESS';

const KNOWN_REGISTRATION_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'REVISION',
  'CANCELLED',
] as const;

type RegistrationStatus = (typeof KNOWN_REGISTRATION_STATUSES)[number];
type ProfileFieldErrors = Partial<Record<'domisili' | 'reason', string>>;
type WhatsAppInvitation = NonNullable<
  NonNullable<ProgramRegistration['participantId']>['whatsappGroup']
>;

function isRegistrationStatus(value: unknown): value is RegistrationStatus {
  return (
    typeof value === 'string' && (KNOWN_REGISTRATION_STATUSES as readonly string[]).includes(value)
  );
}

export function Registration() {
  const { id: registrationReference } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { data: program, isLoading: progLoading } = useRegistrationProgram(
    registrationReference || ''
  );
  const { mutate: register, isPending: submitting } = useRegisterProgram();
  const { data: enrollment, isLoading: enrollLoading } = useProgramEnrollment(program?.id || '');
  const enrolledProgramId = program?.id;
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState<Step>('PROGRAM');

  useEffect(() => {
    if (enrollment && enrolledProgramId && currentStep !== 'SUCCESS') {
      void navigate(`/programs/${enrolledProgramId}`, { replace: true });
    }
  }, [currentStep, enrollment, enrolledProgramId, navigate]);

  const [consent, setConsent] = useState(false);
  const [notes, setNotes] = useState('');
  const [domisili, setDomisili] = useState('');
  const [reason, setReason] = useState('');
  const [profileFieldErrors, setProfileFieldErrors] = useState<ProfileFieldErrors>({});
  const [resultStatus, setResultStatus] = useState<RegistrationStatus | null>(null);
  const [resultRegistrationId, setResultRegistrationId] = useState<string | null>(null);
  const [whatsappInvitation, setWhatsappInvitation] = useState<WhatsAppInvitation | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // The backend's approval guard requires the registrant's user row
  // to have `phone` + `gender` populated (used downstream for WhatsApp
  // group routing and participant-code generation). We mirror that
  // guard on the participant side: if the user submits without phone
  // or gender, the registration will go through but admin approval
  // will fail with a 409. Easier UX is to detect the gap up-front and
  // pop the EditProfileModal so the user fills in the missing fields
  // before submitting.
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const profileMissingFields: ('phone' | 'gender')[] = [];
  if (!user?.phone) profileMissingFields.push('phone');
  if (!user?.gender) profileMissingFields.push('gender');
  const isProfileComplete = profileMissingFields.length === 0;

  const handleRegister = () => {
    if (!program || !consent) return;
    // Backend CreateRegistrationDto requires email + name (used to
    // find-or-create the User row). Pull them from the auth store;
    // the protected route guarantees they're set, but guard anyway.
    if (!user?.email || !user?.name) {
      console.error('Cannot register without an authenticated user profile');
      return;
    }
    // Guard: backend approval requires phone + gender. Surface the
    // EditProfileModal instead of submitting an incomplete record.
    if (!isProfileComplete) {
      setIsProfileEditOpen(true);
      return;
    }
    setSubmitError(null);
    register(
      {
        programId: program.id,
        email: user.email,
        name: user.name,
        phone: user.phone ?? undefined,
        gender: user.gender ?? undefined,
        notes,
        consentGiven: consent,
        domisili,
        reason,
      },
      {
        onSuccess: response => {
          // Stay on this page and show explicit status feedback so the
          // participant knows whether they're waiting for admin approval
          // or were re-submitted after a previous rejection. Navigating
          // straight to /programs leaves the user with no signal — the
          // programs list only shows enrollments (i.e. APPROVED), so a
          // PENDING row shows up as "no programs" and the user thinks
          // nothing happened.
          const status = isRegistrationStatus(response?.status) ? response.status : 'PENDING';
          setResultStatus(status);
          setResultRegistrationId(typeof response?.id === 'string' ? response.id : null);
          setWhatsappInvitation(response.participantId?.whatsappGroup ?? null);
          setCurrentStep('SUCCESS');
        },
        onError: err => {
          // Keep the form state so the user doesn't lose their input
          // on a network blip, and surface the error inline instead of
          // via a native `alert()` (which would break the app's UX).
          const message =
            err instanceof Error ? err.message : 'Pendaftaran gagal. Silakan coba lagi.';
          console.error('Registration failed', err);
          setSubmitError(message);
          setCurrentStep('CONSENT');
        },
      }
    );
  };

  const resetFlow = () => {
    setCurrentStep('PROGRAM');
    setConsent(false);
    setNotes('');
    setDomisili('');
    setReason('');
    setProfileFieldErrors({});
    setResultStatus(null);
    setResultRegistrationId(null);
    setWhatsappInvitation(null);
    setSubmitError(null);
  };

  const steps: { key: Step; label: BilingualType }[] = [
    { key: 'PROGRAM', label: copy.registration.detailTitle },
    { key: 'PROFILE', label: copy.registration.profileTitle },
    { key: 'CONSENT', label: copy.registration.consentTitle },
  ];

  const continueFromProfile = () => {
    const nextErrors: ProfileFieldErrors = {};
    if (!domisili.trim()) {
      nextErrors.domisili = t(copy.registration.cityRequired);
    }
    if (!reason.trim()) {
      nextErrors.reason = t(copy.registration.reasonRequired);
    }
    if (Object.keys(nextErrors).length > 0) {
      setProfileFieldErrors(nextErrors);
      setSubmitError(null);
      return;
    }
    setProfileFieldErrors({});
    setSubmitError(null);
    setCurrentStep('CONSENT');
  };

  if ((progLoading || enrollLoading) && currentStep !== 'SUCCESS') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
        <p className="text-sm font-body text-on-surface-variant">
          <Bilingual {...copy.registration.preparing} />
        </p>
      </div>
    );
  }

  // If we don't have program data we can't render anything below (the
  // SUCCESS branch also depends on `program` for navigation). This
  // intentionally narrows `program` to non-undefined for the rest of
  // the function, including the SUCCESS branch.
  if (!program) return null;

  if (!program.isRegistrationOpen) {
    return (
      <div className="max-w-md mx-auto py-20 px-4 text-center space-y-6 animate-fadeIn">
        <div className="bg-surface-container rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
          <Lock size={40} className="text-on-surface-variant/30" />
        </div>
        <h2 className="text-2xl font-headline font-bold text-on-surface">
          <Bilingual {...copy.registration.regClosedTitle} />
        </h2>
        <p className="text-on-surface-variant leading-relaxed">
          {t(copy.registration.regClosedDesc).replace('{name}', program.name)}
        </p>
        <Link
          to="/programs"
          className="inline-flex items-center gap-2 text-primary font-label font-bold uppercase tracking-widest text-xs hover:underline pt-4"
        >
          <ArrowLeft size={16} />
          <Bilingual {...copy.registration.backToPrograms} />
        </Link>
      </div>
    );
  }

  if (currentStep === 'SUCCESS') {
    const isPending = resultStatus === 'PENDING';
    const isApproved = resultStatus === 'APPROVED';
    const isRejected = resultStatus === 'REJECTED';
    const isRevision = resultStatus === 'REVISION';

    return (
      <div className="max-w-2xl mx-auto py-12 px-4 animate-fadeIn">
        <div className="bg-surface-container-low rounded-3xl p-8 border border-on-surface/5 shadow-level1 text-center space-y-6">
          {isPending && (
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-warning-container rounded-full w-24 h-24 flex items-center justify-center">
                <Hourglass size={40} className="text-on-warning-container" />
              </div>
              <h2 className="text-2xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.successPendingTitle} />
              </h2>
              <p className="text-on-surface-variant leading-relaxed max-w-md">
                <Bilingual {...copy.registration.successPendingDesc} />
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-label text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.common.status} />
                </span>
                <StatusChip label={t(copy.common.registrationStatus.PENDING)} variant="warning" />
              </div>
            </div>
          )}

          {isApproved && (
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-success-container rounded-full w-24 h-24 flex items-center justify-center">
                <CheckCircle2 size={40} className="text-on-success-container" />
              </div>
              <h2 className="text-2xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.successApprovedTitle} />
              </h2>
              <p className="text-on-surface-variant leading-relaxed max-w-md">
                <Bilingual {...copy.registration.successApprovedDesc} />
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-label text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.common.status} />
                </span>
                <StatusChip label={t(copy.common.registrationStatus.APPROVED)} variant="success" />
              </div>
              {whatsappInvitation?.inviteLink ? (
                <div className="w-full max-w-md rounded-2xl bg-primary/10 p-5 text-left">
                  <div className="flex items-start gap-3">
                    <MessageCircle size={22} className="mt-0.5 shrink-0 text-primary" />
                    <div className="min-w-0">
                      <p className="font-label text-sm font-bold text-on-surface">
                        <Bilingual {...copy.registration.whatsappInvitationTitle} />
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-on-surface-variant">
                        <Bilingual {...copy.registration.whatsappInvitationDesc} />
                      </p>
                      <p className="mt-2 text-xs font-bold text-primary">
                        {whatsappInvitation.name}
                      </p>
                      <a
                        href={whatsappInvitation.inviteLink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-4 inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-4 py-3 text-xs font-label font-bold uppercase tracking-widest text-on-primary no-underline transition-colors hover:bg-primary-container focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary active:scale-95"
                      >
                        <MessageCircle size={16} aria-hidden="true" />
                        <Bilingual {...copy.registration.joinWhatsApp} />
                      </a>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="max-w-md text-sm leading-relaxed text-on-surface-variant">
                  <Bilingual {...copy.registration.groupInvitationPending} />
                </p>
              )}
            </div>
          )}

          {isRejected && (
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-error-container rounded-full w-24 h-24 flex items-center justify-center">
                <XCircle size={40} className="text-on-error-container" />
              </div>
              <h2 className="text-2xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.successRejectedTitle} />
              </h2>
              <p className="text-on-surface-variant leading-relaxed max-w-md">
                <Bilingual {...copy.registration.successRejectedDesc} />
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-label text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.common.status} />
                </span>
                <StatusChip label={t(copy.common.registrationStatus.REJECTED)} variant="error" />
              </div>
            </div>
          )}

          {isRevision && (
            <div className="flex flex-col items-center space-y-4">
              <div className="bg-secondary-container rounded-full w-24 h-24 flex items-center justify-center">
                <Info size={40} className="text-on-secondary-container" />
              </div>
              <h2 className="text-2xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.successRevisionTitle} />
              </h2>
              <p className="text-on-surface-variant leading-relaxed max-w-md">
                <Bilingual {...copy.registration.successRevisionDesc} />
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs font-label text-on-surface-variant uppercase tracking-widest">
                  <Bilingual {...copy.common.status} />
                </span>
                <StatusChip label={t(copy.common.registrationStatus.REVISION)} variant="warning" />
              </div>
            </div>
          )}

          {resultRegistrationId && (
            <p className="text-[10px] font-mono text-on-surface-variant/60 break-all">
              ID: {resultRegistrationId}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-6 border-t border-on-surface/5">
            <Button onClick={() => void navigate('/programs')} className="gap-2">
              <Bilingual {...copy.registration.backToPrograms} />
              <ArrowRight size={18} />
            </Button>
            {(isRejected || isRevision) && (
              <Button variant="secondary" onClick={resetFlow} className="gap-2">
                <Bilingual {...copy.registration.tryAgainBtn} />
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4 animate-fadeIn">
      {/* Back button */}
      <Link
        to="/programs"
        className="inline-flex items-center gap-2 text-sm text-on-surface-variant hover:text-primary transition-colors mb-8"
      >
        <ArrowLeft size={16} />
        <Bilingual {...copy.registration.backToExplore} />
      </Link>

      {/* Progress Stepper */}
      <div className="flex items-center justify-between mb-12 relative">
        <div className="absolute top-1/2 left-0 w-full h-0.5 bg-surface-container-high -translate-y-1/2 z-0" />
        {steps.map((step, i) => {
          const isActive = currentStep === step.key;
          const isDone = steps.findIndex(s => s.key === currentStep) > i;

          return (
            <div key={step.key} className="relative z-10 flex flex-col items-center group">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-500 border-2 ${
                  isActive
                    ? 'bg-primary border-primary text-on-primary scale-110 shadow-lg'
                    : isDone
                      ? 'bg-success border-success text-on-success'
                      : 'bg-surface-container border-surface-container-high text-on-surface-variant'
                }`}
              >
                {isDone ? <Check size={18} /> : <span>{i + 1}</span>}
              </div>
              <span
                className={`absolute top-12 whitespace-nowrap text-[10px] font-label uppercase tracking-widest font-bold transition-colors ${
                  isActive ? 'text-primary' : 'text-on-surface-variant'
                }`}
              >
                <Bilingual {...step.label} />
              </span>
            </div>
          );
        })}
      </div>

      {/* Content Area */}
      <div className="mt-16 bg-surface-container-low rounded-3xl p-8 border border-on-surface/5 shadow-level1">
        {currentStep === 'PROGRAM' && (
          <div className="space-y-6 animate-slideInRight">
            {program.coverImage && (
              <div className="relative rounded-2xl overflow-hidden aspect-[21/9] max-h-48 w-full shadow-sm bg-surface-container">
                <img
                  src={resolveMediaUrl(program.coverImage, apiBaseUrl)}
                  alt={program.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="flex items-start gap-4 p-6 bg-primary/5 rounded-2xl border border-primary/10">
              <div className="p-3 bg-primary/10 rounded-xl text-primary">
                <Sparkles size={24} />
              </div>
              <div>
                <h2 className="text-xl font-headline font-bold text-on-surface mb-1">
                  <Bilingual {...copy.registration.confirmingTitle} />
                </h2>
                <p className="text-sm text-on-surface-variant leading-relaxed">
                  {t(copy.registration.confirmingDesc)
                    .split(/(\{name\}|\{code\})/)
                    .map((part, index) => {
                      if (part === '{name}') {
                        return (
                          <span key={index} className="font-bold text-on-surface">
                            {program.name}
                          </span>
                        );
                      }
                      if (part === '{code}') {
                        return (
                          <span
                            key={index}
                            className="font-mono text-xs px-1 bg-surface-container rounded font-bold"
                          >
                            {program.code}
                          </span>
                        );
                      }
                      return part;
                    })}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="p-4 bg-surface-container-lowest rounded-2xl border border-on-surface/5">
                <p className="text-[10px] font-label text-on-surface-variant uppercase mb-1">
                  <Bilingual {...copy.registration.approvalMode} />
                </p>
                <p className="text-sm font-body font-bold">
                  {program.approvalMode === 'AUTO_APPROVE' || program.approvalMode === 'MANUAL'
                    ? t(copy.registration.approvalModeVal[program.approvalMode])
                    : program.approvalMode}
                </p>
              </div>
              <div className="p-4 bg-surface-container-lowest rounded-2xl border border-on-surface/5">
                <p className="text-[10px] font-label text-on-surface-variant uppercase mb-1">
                  <Bilingual {...copy.registration.registrationOpensAt} />
                </p>
                <p className="text-sm font-body font-bold">
                  {program.registrationOpensAt
                    ? new Date(program.registrationOpensAt).toLocaleDateString()
                    : t(copy.common.tbd)}
                </p>
              </div>
              <div className="p-4 bg-surface-container-lowest rounded-2xl border border-on-surface/5">
                <p className="text-[10px] font-label text-on-surface-variant uppercase mb-1">
                  <Bilingual {...copy.registration.registrationClosesAt} />
                </p>
                <p className="text-sm font-body font-bold">
                  {program.registrationClosesAt
                    ? new Date(program.registrationClosesAt).toLocaleDateString()
                    : t(copy.common.tbd)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-2xl bg-secondary-container/40 p-4 text-sm text-on-secondary-container">
              <CalendarDays size={19} className="shrink-0" aria-hidden="true" />
              <span className="font-body">
                <Bilingual {...copy.registration.registrationWindow} />
              </span>
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-on-surface/5">
              <Button onClick={() => setCurrentStep('PROFILE')} className="gap-2">
                <Bilingual {...copy.registration.nextStep} />
                <ArrowRight size={18} />
              </Button>
            </div>
          </div>
        )}

        {currentStep === 'PROFILE' && (
          <div className="space-y-6 animate-slideInRight">
            <div className="flex items-center gap-3 mb-6">
              <Info className="text-primary" />
              <h2 className="text-xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.verifyIdentityTitle} />
              </h2>
            </div>

            <p className="text-sm text-on-surface-variant mb-6">
              <Bilingual {...copy.registration.verifyIdentityDesc} />
            </p>

            <div className="space-y-4">
              {[
                { label: copy.registration.fullName, value: user?.name },
                { label: copy.registration.emailAddress, value: user?.email },
                {
                  label: copy.registration.phone,
                  value: user?.phone || t(copy.registration.phoneNotSet),
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-4 bg-surface-container-lowest rounded-xl border border-on-surface/5"
                >
                  <p className="text-[10px] font-label text-on-surface-variant uppercase mb-1">
                    <Bilingual {...item.label} />
                  </p>
                  <p className="text-sm font-body font-bold text-on-surface">{item.value}</p>
                </div>
              ))}

              <div className="space-y-4 pt-4 border-t border-on-surface/5">
                {submitError && (
                  <div
                    role="alert"
                    className="rounded-xl bg-error-container/40 px-4 py-3 text-sm text-on-error-container"
                  >
                    {submitError}
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                    <Bilingual {...copy.registration.cityLabel} />
                  </label>
                  <CityAutocomplete
                    placeholder={t(copy.registration.cityPlaceholder)}
                    value={domisili}
                    onChange={value => {
                      setDomisili(value);
                      setProfileFieldErrors(current => ({ ...current, domisili: undefined }));
                    }}
                    dropdownLabel={t(copy.registration.cityDropdownLabel)}
                    emptyMessage={query =>
                      t(copy.registration.cityNoResults).replace('{query}', query)
                    }
                    error={profileFieldErrors.domisili}
                  />
                  {profileFieldErrors.domisili && (
                    <p
                      id="registration-city-error"
                      role="alert"
                      className="px-1 text-xs text-error"
                    >
                      {profileFieldErrors.domisili}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest pl-1">
                    <Bilingual {...copy.registration.reasonLabel} />
                  </label>
                  <textarea
                    className={`min-h-[80px] w-full rounded-xl bg-surface-container-lowest p-4 text-sm font-body outline-none focus:ring-2 ${
                      profileFieldErrors.reason
                        ? 'ring-2 ring-error/40 focus:ring-error/40'
                        : 'border border-on-surface/5 focus:ring-primary/20'
                    }`}
                    placeholder={t(copy.registration.reasonPlaceholder)}
                    value={reason}
                    onChange={e => {
                      setReason(e.target.value);
                      setProfileFieldErrors(current => ({ ...current, reason: undefined }));
                    }}
                    aria-invalid={Boolean(profileFieldErrors.reason)}
                    aria-describedby={
                      profileFieldErrors.reason ? 'registration-reason-error' : undefined
                    }
                    required
                  />
                  {profileFieldErrors.reason && (
                    <p
                      id="registration-reason-error"
                      role="alert"
                      className="px-1 text-xs text-error"
                    >
                      {profileFieldErrors.reason}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-6 border-t border-on-surface/5">
              <Button variant="secondary" onClick={() => setCurrentStep('PROGRAM')}>
                <Bilingual {...copy.registration.backBtn} />
              </Button>
              <Button onClick={continueFromProfile} className="gap-2">
                <Bilingual {...copy.registration.verifyContinueBtn} />
                <ArrowRight size={18} />
              </Button>
            </div>
          </div>
        )}

        {currentStep === 'CONSENT' && (
          <div className="space-y-6 animate-slideInRight">
            <div className="flex items-center gap-3 mb-6">
              <ShieldCheck size={28} className="text-success" />
              <h2 className="text-xl font-headline font-bold text-on-surface">
                <Bilingual {...copy.registration.commitmentTitle} />
              </h2>
            </div>

            {/* Profile completeness banner. Shown when the user is */}
            {/* missing phone/gender on their User row — those are     */}
            {/* required for admin approval (WhatsApp group routing,  */}
            {/* participant code generation). Clicking the button      */}
            {/* opens the same EditProfileModal used in /profile.       */}
            {!isProfileComplete && (
              <div
                role="alert"
                className="flex items-start gap-3 p-4 bg-warning-container text-on-warning-container rounded-2xl border border-warning/30"
              >
                <Info size={20} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm font-body leading-relaxed">
                  <p className="font-label font-bold mb-1">
                    {t({
                      id: 'Lengkapi profil Anda terlebih dahulu',
                      ar: 'يُرجى إكمال ملفك الشخصي أولاً',
                    })}
                  </p>
                  <p>
                    {t({
                      id: `Pendaftaran ini membutuhkan ${profileMissingFields
                        .map(f => (f === 'phone' ? 'nomor WhatsApp' : 'jenis kelamin'))
                        .join(' dan ')} untuk dapat disetujui oleh admin.`,
                      ar: `يتطلب هذا التسجيل ${profileMissingFields
                        .map(f => (f === 'phone' ? 'رقم الواتساب' : 'الجنس'))
                        .join(' و ')} لاعتماد المسؤول.`,
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsProfileEditOpen(true)}
                    className="mt-2 text-xs font-label font-bold uppercase tracking-widest underline"
                  >
                    {t({
                      id: 'Lengkapi Profil Sekarang',
                      ar: 'أكمل الملف الآن',
                    })}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-surface-container-lowest rounded-2xl p-6 border border-on-surface/5 space-y-4 max-h-60 overflow-y-auto font-body text-sm leading-relaxed text-on-surface-variant">
              <p>
                <Bilingual {...copy.registration.commitmentText} />
              </p>
              <ul className="list-disc pl-5 space-y-2">
                <li>
                  <Bilingual {...copy.registration.ethics1} />
                </li>
                <li>
                  <Bilingual {...copy.registration.ethics2} />
                </li>
                <li>
                  <Bilingual {...copy.registration.ethics3} />
                </li>
                <li>
                  <Bilingual {...copy.registration.ethics4} />
                </li>
              </ul>
            </div>

            <div className="space-y-4 py-4">
              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-3 p-4 bg-error-container text-on-error-container rounded-2xl border border-error/30 animate-slideInRight"
                >
                  <XCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 text-sm font-body leading-relaxed">
                    <p className="font-label font-bold mb-1">
                      {t(copy.registration.submitErrorTitle)}
                    </p>
                    <p>{submitError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSubmitError(null)}
                    className="text-on-error-container/70 hover:text-on-error-container"
                    aria-label="Dismiss error"
                  >
                    <XCircle size={16} />
                  </button>
                </div>
              )}

              <label className="flex items-center gap-3 cursor-pointer group">
                <div
                  role="checkbox"
                  aria-checked={consent}
                  onClick={() => setConsent(!consent)}
                  className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                    consent
                      ? 'bg-primary border-primary text-on-primary'
                      : 'border-on-surface-variant group-hover:border-primary'
                  }`}
                >
                  {consent && <Check size={16} strokeWidth={3} />}
                </div>
                <span className="text-sm font-body text-on-surface">
                  <Bilingual {...copy.registration.ethicsAccept} />
                </span>
              </label>

              <div className="space-y-2">
                <p className="text-xs font-label text-on-surface-variant uppercase ml-1">
                  <Bilingual {...copy.registration.additionalNotes} />
                </p>
                <textarea
                  className="w-full bg-surface-container-high rounded-2xl p-4 text-sm font-body outline-none focus:ring-2 focus:ring-primary/20 min-h-[100px]"
                  placeholder={t(copy.registration.notesPlaceholder)}
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                />
              </div>
            </div>

            <div className="flex justify-between items-center pt-6 border-t border-on-surface/5">
              <Button variant="secondary" onClick={() => setCurrentStep('PROFILE')}>
                <Bilingual {...copy.registration.backBtn} />
              </Button>
              <Button
                onClick={handleRegister}
                disabled={!consent || submitting || !isProfileComplete}
                className="gap-2 shadow-lg hover:scale-105 transition-transform"
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Sparkles size={18} />
                )}
                <Bilingual {...copy.registration.completeEnrollment} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Profile completion modal — shared with /profile. */}
      {isProfileEditOpen && <EditProfileModal onClose={() => setIsProfileEditOpen(false)} />}
    </div>
  );
}
